import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, GraduationCap, Briefcase, BarChart3, RefreshCw, ArrowLeft, LogOut, Award, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth helper functions
const getAuthHeader = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const isAuthenticated = () => {
  const token = localStorage.getItem('adminToken');
  const expiry = localStorage.getItem('adminTokenExpiry');
  if (!token || !expiry) return false;
  return Date.now() < parseInt(expiry);
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ careerConsultations: 0, corporateTrainings: 0, hireTalents: 0, certifications: 0, total: 0 });
  const [activeTab, setActiveTab] = useState('career');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCertForm, setShowCertForm] = useState(false);
  const [certForm, setCertForm] = useState({
    certificate_id: '',
    candidate_name: '',
    certification_title: '',
    level: '',
    issue_date: '',
    status: 'Processing',
    program: '',
  });

  // Check authentication on mount
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenExpiry');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchData = async (type) => {
    setLoading(true);
    try {
      const endpoints = {
        career: '/api/admin/career-consultations',
        corporate: '/api/admin/corporate-trainings',
        talent: '/api/admin/hire-talents',
        certifications: '/api/admin/certifications'
      };
      const response = await axios.get(`${API_URL}${endpoints[type]}`, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated()) {
      fetchStats();
      fetchData(activeTab);
    }
  }, [activeTab]);

  const handleRefresh = () => {
    fetchStats();
    fetchData(activeTab);
  };

  const formatDate = (item) => {
    const timestamp = item.timestamp || item.createdAt;
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const statCards = [
    { key: 'careerConsultations', label: 'Career Consultations', icon: Users, color: 'from-[#00E5FF] to-[#1DE9B6]' },
    { key: 'corporateTrainings', label: 'Corporate Trainings', icon: GraduationCap, color: 'from-[#1DE9B6] to-[#38BDF8]' },
    { key: 'hireTalents', label: 'Hiring Requests', icon: Briefcase, color: 'from-[#38BDF8] to-[#00E5FF]' },
    { key: 'certifications', label: 'Certifications', icon: Award, color: 'from-[#00E5FF] to-[#38BDF8]' },
    { key: 'total', label: 'Total Submissions', icon: BarChart3, color: 'from-[#00E5FF] to-[#38BDF8]' }
  ];

  const tabs = [
    { key: 'career', label: 'Career Consultations' },
    { key: 'corporate', label: 'Corporate Trainings' },
    { key: 'talent', label: 'Hiring Requests' },
    { key: 'certifications', label: 'Certifications' }
  ];

  const handleCreateCert = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/admin/certifications`, certForm, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      if (response.data.success) {
        toast.success('Certification created successfully');
        setShowCertForm(false);
        setCertForm({ certificate_id: '', candidate_name: '', certification_title: '', level: '', issue_date: '', status: 'Processing', program: '' });
        fetchData('certifications');
        fetchStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create certification');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <header className="bg-[#111827] border-b border-[#00E5FF]/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center text-[#00E5FF] hover:text-[#1DE9B6] transition-colors">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Site
            </Link>
            <h1 className="text-2xl font-bold text-white font-['Space_Grotesk']">Admin Dashboard</h1>
          </div>
          <div className="flex items-center space-x-3">
            <Button onClick={handleRefresh} className="bg-[#00E5FF]/20 hover:bg-[#00E5FF]/30 text-[#00E5FF] border border-[#00E5FF]/30">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleLogout} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.key} className="bg-[#111827] border-2 border-[#00E5FF]/20 hover:border-[#00E5FF]/40 transition-all" data-testid={`stat-card-${stat.key}`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-[#9CA3AF]">{stat.label}</CardTitle>
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-[#0B0F1A]" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats[stat.key]}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex space-x-2 mb-6">
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`${activeTab === tab.key 
                ? 'bg-[#00E5FF] text-[#0B0F1A]' 
                : 'bg-[#111827] text-[#9CA3AF] border border-[#00E5FF]/20 hover:border-[#00E5FF]/40'
              } transition-all`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </Button>
          ))}
          {activeTab === 'certifications' && (
            <Button
              onClick={() => setShowCertForm(true)}
              className="bg-[#1DE9B6] hover:bg-[#00E5FF] text-[#0B0F1A] font-semibold ml-auto"
              data-testid="add-cert-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Certification
            </Button>
          )}
        </div>

        <Card className="bg-[#111827] border-2 border-[#00E5FF]/20">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-[#00E5FF] animate-spin" />
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-12 text-[#9CA3AF]">
                No submissions yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="submissions-table">
                  <thead className="bg-[#0B0F1A] border-b border-[#00E5FF]/20">
                    <tr>
                      {activeTab === 'career' && (
                        <>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Name</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Email</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Mobile</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Status</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Domain</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Date</th>
                        </>
                      )}
                      {activeTab === 'corporate' && (
                        <>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Organization</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Contact</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Email</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Requirement</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Participants</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Date</th>
                        </>
                      )}
                      {activeTab === 'talent' && (
                        <>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Company</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Contact</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Email</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Requirement</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Positions</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Date</th>
                        </>
                      )}
                      {activeTab === 'certifications' && (
                        <>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Certificate ID</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Candidate</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Title</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Level</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Status</th>
                          <th className="text-left px-4 py-3 text-[#00E5FF] font-semibold">Issue Date</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, index) => (
                      <tr key={index} className="border-b border-[#00E5FF]/10 hover:bg-[#00E5FF]/5 transition-colors">
                        {activeTab === 'career' && (
                          <>
                            <td className="px-4 py-3 text-white">{item.name}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.email}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.mobile}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.currentStatus}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.interestedDomain}</td>
                            <td className="px-4 py-3 text-[#9CA3AF] text-sm">{formatDate(item)}</td>
                          </>
                        )}
                        {activeTab === 'corporate' && (
                          <>
                            <td className="px-4 py-3 text-white">{item.organizationName}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.contactPerson}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.email}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.trainingRequirement}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.participants}</td>
                            <td className="px-4 py-3 text-[#9CA3AF] text-sm">{formatDate(item)}</td>
                          </>
                        )}
                        {activeTab === 'talent' && (
                          <>
                            <td className="px-4 py-3 text-white">{item.companyName}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.contactPerson}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.email}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.hiringRequirement}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.positions}</td>
                            <td className="px-4 py-3 text-[#9CA3AF] text-sm">{formatDate(item)}</td>
                          </>
                        )}
                        {activeTab === 'certifications' && (
                          <>
                            <td className="px-4 py-3 text-[#1DE9B6] font-mono font-bold">{item.certificate_id}</td>
                            <td className="px-4 py-3 text-white">{item.candidate_name}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.certification_title}</td>
                            <td className="px-4 py-3 text-[#9CA3AF]">{item.level}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.status === 'Active' ? 'bg-[#1DE9B6]/20 text-[#1DE9B6]' : 'bg-yellow-500/20 text-yellow-400'
                              }`}>{item.status}</span>
                            </td>
                            <td className="px-4 py-3 text-[#9CA3AF] text-sm">{item.issue_date}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Certification Dialog */}
        <Dialog open={showCertForm} onOpenChange={setShowCertForm}>
          <DialogContent className="max-w-lg bg-[#111827] border-2 border-[#00E5FF]/30" data-testid="add-cert-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white font-['Space_Grotesk']">Add New Certification</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCert} className="space-y-4 mt-2">
              <div>
                <Label className="text-[#F9FAFB]">Certificate ID *</Label>
                <Input value={certForm.certificate_id} onChange={(e) => setCertForm({...certForm, certificate_id: e.target.value})} required placeholder="CXAI-AI-2026-0001" className="mt-1 bg-[#0B0F1A] border-[#1F2937] text-white placeholder:text-[#9CA3AF] focus:border-[#00E5FF]" data-testid="cert-id-input" />
              </div>
              <div>
                <Label className="text-[#F9FAFB]">Candidate Name *</Label>
                <Input value={certForm.candidate_name} onChange={(e) => setCertForm({...certForm, candidate_name: e.target.value})} required placeholder="Full name" className="mt-1 bg-[#0B0F1A] border-[#1F2937] text-white placeholder:text-[#9CA3AF] focus:border-[#00E5FF]" data-testid="cert-name-input" />
              </div>
              <div>
                <Label className="text-[#F9FAFB]">Certification Title *</Label>
                <Select value={certForm.certification_title} onValueChange={(v) => setCertForm({...certForm, certification_title: v})}>
                  <SelectTrigger className="mt-1 bg-[#0B0F1A] border-[#1F2937] text-white focus:border-[#00E5FF]">
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#00E5FF]/30">
                    <SelectItem value="AI Associate" className="text-white focus:bg-[#00E5FF]/20">AI Associate</SelectItem>
                    <SelectItem value="Data Engineer" className="text-white focus:bg-[#00E5FF]/20">Data Engineer</SelectItem>
                    <SelectItem value="ML Engineer" className="text-white focus:bg-[#00E5FF]/20">ML Engineer</SelectItem>
                    <SelectItem value="AI Professional" className="text-white focus:bg-[#00E5FF]/20">AI Professional</SelectItem>
                    <SelectItem value="Cloud Associate" className="text-white focus:bg-[#00E5FF]/20">Cloud Associate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#F9FAFB]">Level *</Label>
                  <Select value={certForm.level} onValueChange={(v) => setCertForm({...certForm, level: v})}>
                    <SelectTrigger className="mt-1 bg-[#0B0F1A] border-[#1F2937] text-white focus:border-[#00E5FF]">
                      <SelectValue placeholder="Level" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#00E5FF]/30">
                      <SelectItem value="Associate" className="text-white focus:bg-[#00E5FF]/20">Associate</SelectItem>
                      <SelectItem value="Professional" className="text-white focus:bg-[#00E5FF]/20">Professional</SelectItem>
                      <SelectItem value="Expert" className="text-white focus:bg-[#00E5FF]/20">Expert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#F9FAFB]">Program *</Label>
                  <Select value={certForm.program} onValueChange={(v) => setCertForm({...certForm, program: v})}>
                    <SelectTrigger className="mt-1 bg-[#0B0F1A] border-[#1F2937] text-white focus:border-[#00E5FF]">
                      <SelectValue placeholder="Program" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#00E5FF]/30">
                      <SelectItem value="AI" className="text-white focus:bg-[#00E5FF]/20">AI</SelectItem>
                      <SelectItem value="DE" className="text-white focus:bg-[#00E5FF]/20">DE (Data Engineering)</SelectItem>
                      <SelectItem value="ML" className="text-white focus:bg-[#00E5FF]/20">ML</SelectItem>
                      <SelectItem value="CL" className="text-white focus:bg-[#00E5FF]/20">CL (Cloud)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#F9FAFB]">Issue Date *</Label>
                  <Input type="date" value={certForm.issue_date} onChange={(e) => setCertForm({...certForm, issue_date: e.target.value})} required className="mt-1 bg-[#0B0F1A] border-[#1F2937] text-white focus:border-[#00E5FF]" data-testid="cert-date-input" />
                </div>
                <div>
                  <Label className="text-[#F9FAFB]">Status *</Label>
                  <Select value={certForm.status} onValueChange={(v) => setCertForm({...certForm, status: v})}>
                    <SelectTrigger className="mt-1 bg-[#0B0F1A] border-[#1F2937] text-white focus:border-[#00E5FF]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#00E5FF]/30">
                      <SelectItem value="Processing" className="text-white focus:bg-[#00E5FF]/20">Processing</SelectItem>
                      <SelectItem value="Active" className="text-white focus:bg-[#00E5FF]/20">Active</SelectItem>
                      <SelectItem value="Revoked" className="text-white focus:bg-[#00E5FF]/20">Revoked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold py-5" data-testid="cert-submit-btn">
                Create Certification
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminDashboard;
